package br.com.texeratx.boardgametimeapp.ui.screens.myclans

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.text.input.OffsetMapping
import androidx.compose.ui.text.input.TransformedText
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.unit.dp
import br.com.texeratx.boardgametimeapp.data.repository.ClanRepository
import br.com.texeratx.boardgametimeapp.ui.components.designsystem.AppScreenScaffold
import br.com.texeratx.boardgametimeapp.ui.components.designsystem.AppTopBarColors
import cafe.adriel.voyager.core.screen.Screen
import cafe.adriel.voyager.navigator.LocalNavigator
import cafe.adriel.voyager.navigator.currentOrThrow
import kotlinx.coroutines.launch
import org.koin.compose.getKoin

// ── Transformação visual: BGT84VF9RKT → BGT-84VF-9RKT ───────────────────────
private object InviteCodeVisualTransformation : VisualTransformation {
    override fun filter(text: AnnotatedString): TransformedText {
        val raw = text.text // máx 11 chars alfanuméricos
        val out = buildString {
            raw.forEachIndexed { i, c ->
                if (i == 3 || i == 7) append('-')
                append(c)
            }
        }
        val mapping = object : OffsetMapping {
            override fun originalToTransformed(offset: Int): Int = when {
                offset <= 3 -> offset
                offset <= 7 -> offset + 1
                else        -> offset + 2
            }
            override fun transformedToOriginal(offset: Int): Int = when {
                offset <= 3 -> offset
                offset <= 8 -> (offset - 1).coerceAtLeast(3)
                else        -> (offset - 2).coerceAtLeast(7)
            }
        }
        return TransformedText(AnnotatedString(out), mapping)
    }
}

// ── Formata com traços para enviar à API ────────────────────────────────────
private fun toApiCode(raw: String): String = buildString {
    raw.forEachIndexed { i, c ->
        if (i == 3 || i == 7) append('-')
        append(c)
    }
}

class JoinClanScreen : Screen {

    @OptIn(ExperimentalMaterial3Api::class)
    @Composable
    override fun Content() {
        val navigator = LocalNavigator.currentOrThrow
        val repo = getKoin().get<ClanRepository>()
        val scope = rememberCoroutineScope()

        // estado = apenas chars alfanuméricos, sem traços, máx 11
        var rawCode by remember { mutableStateOf("") }
        var loading by remember { mutableStateOf(false) }
        var error by remember { mutableStateOf<String?>(null) }
        var showPendingDialog by remember { mutableStateOf(false) }

        val isComplete = rawCode.length == 11

        AppScreenScaffold(
            topBar = {
                TopAppBar(
                    title = { Text("Entrar em clã", fontWeight = FontWeight.Bold) },
                    navigationIcon = {
                        IconButton(onClick = { navigator.pop() }) {
                            Icon(Icons.Default.ArrowBack, contentDescription = "Voltar")
                        }
                    },
                    colors = AppTopBarColors()
                )
            }
        ) { padding ->
            Column(
                Modifier
                    .padding(padding)
                    .fillMaxSize()
                    .padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                Text("Digite o código de convite (formato BGT-XXXX-XXXX)")

                OutlinedTextField(
                    value = rawCode,
                    onValueChange = { input ->
                        // guarda só alfanuméricos em maiúsculo, máx 11
                        rawCode = input
                            .filter { it.isLetterOrDigit() }
                            .uppercase()
                            .take(11)
                        error = null
                    },
                    label = { Text("Código de convite") },
                    placeholder = { Text("BGT-XXXX-XXXX") },
                    singleLine = true,
                    visualTransformation = InviteCodeVisualTransformation,
                    keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Characters),
                    modifier = Modifier.fillMaxWidth()
                )

                error?.let { Text(it, color = MaterialTheme.colorScheme.error) }

                Button(
                    onClick = {
                        scope.launch {
                            loading = true
                            error = null
                            repo.joinClan(toApiCode(rawCode)).fold(
                                onSuccess = { resp ->
                                    loading = false
                                    if (resp.status == "pending") {
                                        showPendingDialog = true
                                    } else {
                                        navigator.pop()
                                    }
                                },
                                onFailure = {
                                    loading = false
                                    error = it.message ?: "Código inválido"
                                }
                            )
                        }
                    },
                    enabled = !loading && isComplete,
                    modifier = Modifier.fillMaxWidth()
                ) { Text(if (loading) "Enviando..." else "Entrar") }

                if (showPendingDialog) {
                    AlertDialog(
                        onDismissRequest = { },
                        title = { Text("Pedido enviado") },
                        text = {
                            Text("Seu pedido de entrada foi enviado. Aguarde a aprovação de um administrador do clã.")
                        },
                        confirmButton = {
                            TextButton(onClick = {
                                showPendingDialog = false
                                navigator.pop()
                            }) {
                                Text("OK")
                            }
                        }
                    )
                }
            }
        }
    }
}
